import { useState } from "react";
import { Badge } from "@/components/ui/badge";
interface StaffMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  position: string;
  contractType: string; // Added contractType
  startDate: string;
  salary: number;
  certifications: string[];
  skills: string[];
  emergencyContact: string;
  notes: string;
}

// Mock data for staff members
const mockStaff: StaffMember[] = [
  {
    id: "1",
    name: "John Doe",
    email: "john.doe@example.com",
    phone: "123-456-7890",
    position: "Marine Biologist",
    contractType: "Full-Time",
    startDate: "2022-01-15",
    salary: 60000,
    certifications: ["Certified Marine Biologist", "First Aid"],
    skills: ["Marine Ecology", "Research", "Public Speaking"],
    emergencyContact: "Jane Doe - 987-654-3210",
    notes: "Expert in whale behavior and ecology."
  },
  {
    id: "2",
    name: "Jane Smith",
    email: "jane.smith@example.com",
    phone: "123-456-7891",
    position: "Wildlife Tour Guide",
    contractType: "Part-Time",
    startDate: "2022-03-01",
    salary: 25000,
    certifications: ["Wildlife Guide Certification", "First Aid"],
    skills: ["Customer Service", "Animal Handling", "Navigation"],
    emergencyContact: "John Smith - 987-654-3211",
    notes: "Great with customers and knowledgeable about local wildlife."
  },
  {
    id: "3",
    name: "Emily Johnson",
    email: "emily.johnson@example.com",
    phone: "123-456-7892",
    position: "Customer Service Representative",
    contractType: "Full-Time",
    startDate: "2021-06-15",
    salary: 40000,
    certifications: ["Customer Service Excellence"],
    skills: ["Communication", "Problem Solving", "Sales"],
    emergencyContact: "Michael Johnson - 987-654-3212",
    notes: "Handles bookings and customer inquiries."
  },
  {
    id: "4",
    name: "Michael Brown",
    email: "michael.brown@example.com",
    phone: "123-456-7893",
    position: "Boat Captain",
    contractType: "Full-Time",
    startDate: "2020-05-20",
    salary: 70000,
    certifications: ["Captain's License", "First Aid"],
    skills: ["Navigation", "Safety Management", "Leadership"],
    emergencyContact: "Sarah Brown - 987-654-3213",
    notes: "Experienced captain with over 10 years of experience."
  },
  {
    id: "5",
    name: "Sarah Davis",
    email: "sarah.davis@example.com",
    phone: "123-456-7894",
    position: "Marketing Manager",
    contractType: "Full-Time",
    startDate: "2021-09-01",
    salary: 55000,
    certifications: ["Digital Marketing Certification"],
    skills: ["SEO", "Content Creation", "Social Media Management"],
    emergencyContact: "David Davis - 987-654-3214",
    notes: "Responsible for promoting whale-watching tours."
  },
  {
    id: "6",
    name: "David Wilson",
    email: "david.wilson@example.com",
    phone: "123-456-7895",
    position: "Maintenance Technician",
    contractType: "Part-Time",
    startDate: "2022-02-10",
    salary: 20000,
    certifications: ["Marine Equipment Maintenance"],
    skills: ["Mechanical Skills", "Problem Solving"],
    emergencyContact: "Laura Wilson - 987-654-3215",
    notes: "Maintains boats and equipment."
  },
  {
    id: "7",
    name: "Laura Martinez",
    email: "laura.martinez@example.com",
    phone: "123-456-7896",
    position: "Intern",
    contractType: "Internship",
    startDate: "2023-05-01",
    salary: 15000,
    certifications: [],
    skills: ["Research", "Assistance"],
    emergencyContact: "Carlos Martinez - 987-654-3216",
    notes: "Assists with research and customer service."
  },
  {
    id: "8",
    name: "James Taylor",
    email: "james.taylor@example.com",
    phone: "123-456-7897",
    position: "Safety Officer",
    contractType: "Full-Time",
    startDate: "2021-04-15",
    salary: 50000,
    certifications: ["Safety Management Certification"],
    skills: ["Risk Assessment", "Emergency Response"],
    emergencyContact: "Patricia Taylor - 987-654-3217",
    notes: "Ensures safety protocols are followed."
  },
  {
    id: "9",
    name: "Patricia Lee",
    email: "patricia.lee@example.com",
    phone: "123-456-7898",
    position: "Reservation Specialist",
    contractType: "Part-Time",
    startDate: "2022-07-01",
    salary: 22000,
    certifications: ["Reservation Management"],
    skills: ["Customer Service", "Organization"],
    emergencyContact: "Robert Lee - 987-654-3218",
    notes: "Handles online and phone reservations."
  },
  {
    id: "10",
    name: "Robert Garcia",
    email: "robert.garcia@example.com",
    phone: "123-456-7899",
    position: "Tour Coordinator",
    contractType: "Full-Time",
    startDate: "2020-11-01",
    salary: 48000,
    certifications: ["Tour Management Certification"],
    skills: ["Planning", "Coordination"],
    emergencyContact: "Jessica Garcia - 987-654-3219",
    notes: "Coordinates all tour activities and schedules."
  },
];

// Determine pending actions
const getPendingActions = (member: StaffMember) => {
  const actions: string[] = [];
  const today = new Date();
  const startDate = new Date(member.startDate);

  // Contract-specific actions
  if (member.contractType === "Part-Time" || member.contractType === "Internship") {
    const oneYearLater = new Date(startDate);
    oneYearLater.setFullYear(startDate.getFullYear() + 1);

    if (today > oneYearLater) {
      actions.push("Renew Contract");
    }
  }

  // Missing certifications
  if (member.certifications.length === 0) {
    actions.push("Add Required Certifications");
  }

  // Example: Certification-specific alert
  if (member.certifications.includes("First Aid") && Math.random() > 0.5) {
    actions.push("Renew First Aid Certification");
  }

  return actions;
};

export function StaffTable({ searchTerm }: { searchTerm: string }) {
  const [staff] = useState<StaffMember[]>(mockStaff);

  return (
    <div className="overflow-auto border rounded-lg shadow-md">
      <table className="table-auto w-full text-sm bg-white">
        <thead className="bg-gray-200">
          <tr>
            <th className="p-3 text-left">Name</th>
            <th className="p-3 text-left">Position</th>
            <th className="p-3 text-left">Contract</th>
            <th className="p-3 text-left">Start Date</th>
            <th className="p-3 text-left">Salary</th>
            <th className="p-3 text-left">Pending Actions</th>
          </tr>
        </thead>
        <tbody>
          {staff
            .filter((member) =>
              member.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .map((member) => {
              const pendingActions = getPendingActions(member);

              return (
                <tr key={member.id} className="border-b">
                  <td className="p-3">{member.name}</td>
                  <td className="p-3">{member.position}</td>
                  <td className="p-3">
                    <Badge
                      className={`${
                        member.contractType === "Full-Time"
                          ? "bg-green-100 text-green-600"
                          : "bg-yellow-100 text-yellow-600"
                      }`}
                    >
                      {member.contractType}
                    </Badge>
                  </td>
                  <td className="p-3">
                    {new Date(member.startDate).toLocaleDateString()}
                  </td>
                  <td className="p-3">${member.salary.toLocaleString()}</td>
                  <td className="p-3">
                    {pendingActions.length > 0 ? (
                      <ul className="list-disc pl-4 space-y-1">
                        {pendingActions.map((action, index) => (
                          <li key={index} className="text-gray-700">
                            {action}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-gray-500">No actions needed</span>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}